import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import { ErrorBoundaryProps } from 'app/components/layouts/ErrorBoundary/ErrorBoundary.types';
import SilentErrorBoundary from 'app/components/layouts/ErrorBoundary/strategies/SilentErrorBoundary';
import useFeatureFlags from 'app/hooks/useFeatureFlags';
import { closeSentry, initSentry } from 'app/services/sentry';
import { useEffect, useState } from 'react';

function ErrorBoundary({ children, fallback }: ErrorBoundaryProps) {
  const flags = useFeatureFlags();

  const [useSentry, setUseSentry] = useState(false);
  const [isSentryReady, setIsSentryReady] = useState(false);

  useEffect(() => {
    async function enableSentry() {
      await initSentry();
      setIsSentryReady(true);
    }

    async function disableSentry() {
      setIsSentryReady(false);
      await closeSentry();
    }

    if (useSentry) {
      if (!isSentryReady) {
        enableSentry();
      }
    } else {
      disableSentry();
    }
  }, [useSentry, isSentryReady]);

  useEffect(() => {
    if (flags.uiErrorBoundaryStrategy === ERROR_BOUNDARY_STRATEGIES.SENTRY) {
      setUseSentry(true);
    }
  }, [flags.uiErrorBoundaryStrategy]);

  return useSentry ? (
    isSentryReady ? (
      <SentryErrorBoundary fallback={fallback}>{children}</SentryErrorBoundary>
    ) : null
  ) : (
    <SilentErrorBoundary fallback={fallback}>{children}</SilentErrorBoundary>
  );
}

/**
 * We can now use two different error boundaries:
 * - Sentry's, the one we've always used
 * - Silent, a boundary that does not export error logs to any third party
 */
export enum ERROR_BOUNDARY_STRATEGIES {
  SENTRY = 'sentry',
  SILENT = 'silent',
}

export default ErrorBoundary;
